import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../services/api';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Receipt,
  Users,
  Building2,
  AlertTriangle,
  ArrowRight,
  FolderKanban,
  RefreshCw,
} from 'lucide-react';

interface ExchangeRateInfo {
  base_currency: string;
  target_currency: string;
  rate: number;
  rate_date: string;
  source: string;
}

interface DashboardSummary {
  total_revenue: number;
  total_expenses: number;
  net_profit: number;
  outstanding_invoices: number;
  overdue_invoices: number;
  pending_expenses: number;
  invoices_count: number;
  expenses_count: number;
  clients_count: number;
  vendors_count: number;
  base_currency: string;
  exchange_rates_used: ExchangeRateInfo[];
}

interface OverdueInvoice {
  id: string;
  invoice_number: string;
  client_name: string;
  amount_due: number;
  due_date: string;
  days_overdue: number;
}

interface RecentActivity {
  id: string;
  type: string;
  description: string;
  amount: number | null;
  currency: string | null;
  date: string;
}

interface DashboardOverview {
  summary: DashboardSummary;
  overdue_invoices: OverdueInvoice[];
  recent_activity: RecentActivity[];
}

interface BudgetWarning {
  project_id: string;
  project_name: string;
  budget: number;
  budget_currency: string;
  used_amount: number;
  used_percent: number;
  is_exceeded: boolean;
}

interface OverdueInvoiceAlert {
  id: string;
  invoice_number: string;
  client_name: string;
  amount_due: number;
  currency: string;
  days_overdue: number;
}

interface DashboardAlerts {
  budget_warnings: BudgetWarning[];
  overdue_invoices: OverdueInvoiceAlert[];
}

function formatCurrency(amount: number, currency = 'ZAR'): string {
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  return `${symbol}${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [alerts, setAlerts] = useState<DashboardAlerts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overview, alertsData] = await Promise.all([
          apiGet<DashboardOverview>('/api/dashboard/overview'),
          apiGet<DashboardAlerts>('/api/dashboard/alerts'),
        ]);
        setData(overview);
        setAlerts(alertsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { summary, overdue_invoices, recent_activity } = data;

  const stats = [
    {
      name: 'Total Revenue',
      value: formatCurrency(summary.total_revenue),
      icon: TrendingUp,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
    },
    {
      name: 'Total Expenses',
      value: formatCurrency(summary.total_expenses),
      icon: TrendingDown,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
    },
    {
      name: 'Net Profit',
      value: formatCurrency(summary.net_profit),
      icon: DollarSign,
      color: summary.net_profit >= 0 ? 'text-green-400' : 'text-red-400',
      bg: summary.net_profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
    },
    {
      name: 'Outstanding',
      value: formatCurrency(summary.outstanding_invoices),
      icon: FileText,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
    },
  ];

  const counts = [
    { name: 'Invoices', value: summary.invoices_count, icon: FileText, href: '/admin/invoices' },
    { name: 'Expenses', value: summary.expenses_count, icon: Receipt, href: '/admin/expenses' },
    { name: 'Clients', value: summary.clients_count, icon: Users, href: '/admin/clients' },
    { name: 'Vendors', value: summary.vendors_count, icon: Building2, href: '/admin/vendors' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400">Overview of your finances</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-dark-900 rounded-xl border border-dark-800 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">{stat.name}</p>
                <p className={`text-2xl font-bold ${stat.color} mt-1`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Exchange Rate Info */}
      {summary.exchange_rates_used && summary.exchange_rates_used.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-dark-900 rounded-xl border border-dark-800">
          <RefreshCw className="w-4 h-4 text-primary-500 flex-shrink-0" />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {summary.exchange_rates_used.map((rate) => (
              <span key={`${rate.base_currency}-${rate.target_currency}`} className="text-gray-300">
                1 {rate.base_currency} ={' '}
                <span className="text-white font-medium">
                  R{rate.rate.toLocaleString('en-ZA', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                </span>
                <span className="text-gray-500 ml-1">
                  via {rate.source.toUpperCase()} ({rate.rate_date})
                </span>
              </span>
            ))}
          </div>
          <span className="text-xs text-gray-500 ml-auto flex-shrink-0">All amounts in ZAR</span>
        </div>
      )}

      {/* Counts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {counts.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="bg-dark-900 rounded-xl border border-dark-800 p-4 hover:border-primary-500/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-dark-800 group-hover:bg-primary-500/10">
                <item.icon className="w-5 h-5 text-gray-400 group-hover:text-primary-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{item.value}</p>
                <p className="text-sm text-gray-400">{item.name}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Budget Warnings */}
      {alerts && alerts.budget_warnings.length > 0 && (
        <div className="bg-dark-900 rounded-xl border border-dark-800">
          <div className="p-4 border-b border-dark-800 flex items-center justify-between">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-orange-400" />
              Budget Warnings
            </h2>
            <Link
              to="/admin/projects"
              className="text-sm text-primary-500 hover:text-primary-400 flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="p-4 space-y-4">
            {alerts.budget_warnings.map((warning) => (
              <Link
                key={warning.project_id}
                to={`/admin/projects/${warning.project_id}`}
                className="block p-3 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">{warning.project_name}</span>
                  <span
                    className={`text-sm font-medium ${
                      warning.is_exceeded ? 'text-red-400' : 'text-orange-400'
                    }`}
                  >
                    {warning.used_percent.toFixed(0)}% used
                  </span>
                </div>
                <div className="relative h-2 bg-dark-700 rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                      warning.is_exceeded ? 'bg-red-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${Math.min(warning.used_percent, 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>
                    {formatCurrency(warning.used_amount, warning.budget_currency)} of{' '}
                    {formatCurrency(warning.budget, warning.budget_currency)}
                  </span>
                  {warning.is_exceeded && (
                    <span className="text-red-400">Budget exceeded!</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Overdue Invoices */}
        <div className="bg-dark-900 rounded-xl border border-dark-800">
          <div className="p-4 border-b border-dark-800 flex items-center justify-between">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Overdue Invoices
            </h2>
            <Link
              to="/admin/invoices?status=overdue"
              className="text-sm text-primary-500 hover:text-primary-400 flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-dark-800">
            {overdue_invoices.length === 0 ? (
              <p className="p-4 text-gray-500 text-center">No overdue invoices</p>
            ) : (
              overdue_invoices.slice(0, 5).map((invoice) => (
                <Link
                  key={invoice.id}
                  to={`/admin/invoices/${invoice.id}`}
                  className="p-4 flex items-center justify-between hover:bg-dark-800/50"
                >
                  <div>
                    <p className="font-medium text-white">{invoice.invoice_number}</p>
                    <p className="text-sm text-gray-400">{invoice.client_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-red-400">
                      {formatCurrency(invoice.amount_due)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {invoice.days_overdue} days overdue
                    </p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-dark-900 rounded-xl border border-dark-800">
          <div className="p-4 border-b border-dark-800">
            <h2 className="font-semibold text-white">Recent Activity</h2>
          </div>
          <div className="divide-y divide-dark-800">
            {recent_activity.length === 0 ? (
              <p className="p-4 text-gray-500 text-center">No recent activity</p>
            ) : (
              recent_activity.slice(0, 5).map((activity) => (
                <div key={activity.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        activity.type === 'invoice'
                          ? 'bg-blue-500/10'
                          : activity.type === 'expense'
                          ? 'bg-red-500/10'
                          : 'bg-green-500/10'
                      }`}
                    >
                      {activity.type === 'invoice' ? (
                        <FileText className="w-4 h-4 text-blue-400" />
                      ) : activity.type === 'expense' ? (
                        <Receipt className="w-4 h-4 text-red-400" />
                      ) : (
                        <DollarSign className="w-4 h-4 text-green-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-white">{activity.description}</p>
                      <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
                    </div>
                  </div>
                  {activity.amount && (
                    <p
                      className={`text-sm font-medium ${
                        activity.type === 'expense' ? 'text-red-400' : 'text-green-400'
                      }`}
                    >
                      {activity.type === 'expense' ? '-' : '+'}
                      {formatCurrency(activity.amount, activity.currency || 'ZAR')}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
